import { CheckCircle2, Circle, CircleDot, PauseCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import type { ContractGuideView } from "./api"
import { Pill } from "./components"
import { CONTRACT_STATE_LABEL, fmtEur, fmtShort } from "./lib"

const PAYMENT_STATE: Record<string, string> = {
  planned: "Previsto",
  previsto: "Previsto",
  requested: "Pedido",
  pedido: "Pedido",
  invoiced: "Faturado",
  faturado: "Faturado",
  paid: "Pago",
  pago: "Pago",
}

function when(iso: string | null | undefined): string {
  return iso ? fmtShort(iso.slice(0, 10)) : ""
}

/** Estado do contrato para a pastilha do Detalhe (modo `host`). */
export function contractStateOf(guide: ContractGuideView): { label: string; tone: "success" | "warning" | "danger" | "neutral" | "info" } {
  if (guide.on_hold) return { label: "Parada", tone: "danger" }
  const gs = guide.general_state ?? ""
  const label = CONTRACT_STATE_LABEL[gs] ?? (gs || guide.status)
  const tone = gs === "concluido" ? "success" : gs === "em_execucao" ? "warning" : gs === "cancelado" ? "neutral" : "info"
  return { label, tone }
}

/**
 * «Resumo» do Detalhe em modo `host`: o guia do contrato do Koortex — fases,
 * passos e quem os fechou, paragem e pagamentos. Só leitura: a app escreve
 * pelas ações (atribuir, campo); a adjudicação vem do pagamento no Odoo; o
 * resto é no Koortex.
 */
export function ContractGuidePanel({ guide }: { guide: ContractGuideView }) {
  return (
    <section className="space-y-3 rounded-xl border p-4 border-border">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Contrato no Koortex</h2>
        <span className="text-xs text-muted-foreground">
          {guide.guide.progress.done}/{guide.guide.progress.total} passos
          {guide.current_phase && ` · fase ${guide.current_phase.index + 1} de ${guide.current_phase.total}`}
        </span>
      </div>

      {guide.on_hold && (
        <p className="flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <PauseCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Parado desde {when(guide.on_hold.since)}
            {guide.on_hold.reason_label && ` · ${guide.on_hold.reason_label}`}
            {guide.on_hold.note && ` · ${guide.on_hold.note}`}
          </span>
        </p>
      )}
      {guide.warnings.length > 0 && (
        <ul className="space-y-1 text-xs text-amber-800">
          {guide.warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}

      <ol className="space-y-3">
        {guide.guide.phases.map((phase) => (
          <li key={phase.stage_key} className="min-w-0">
            <div className="flex items-center gap-2">
              {phase.state === "done" ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-700" />
              ) : phase.state === "current" ? (
                <CircleDot className="h-4 w-4 shrink-0 text-blue-700" />
              ) : (
                <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className={cn("text-sm font-medium", phase.state === "future" && "text-muted-foreground")}>{phase.name}</span>
              {phase.state === "current" && <Pill tone="info">Fase atual</Pill>}
            </div>
            <ul className="ml-6 mt-1 space-y-1">
              {phase.steps.map((step) => (
                <li key={step.id} className="text-xs">
                  <div className="flex flex-wrap items-baseline gap-x-1.5">
                    <span className={cn(step.state === "done" ? "text-foreground" : "text-muted-foreground")}>
                      {step.state === "done" ? "✓" : "○"} {step.label}
                      {step.blocking && step.state !== "done" && phase.state === "current" && " · obrigatório"}
                    </span>
                    {step.done_by && (
                      <span className="text-muted-foreground">
                        — {step.done_by.name ?? (step.done_by.kind === "app" ? "app" : step.done_by.kind)}
                        {step.done_by.at && `, ${when(step.done_by.at)}`}
                      </span>
                    )}
                    {step.missing && step.missing.length > 0 && step.state !== "done" && (
                      <span className="text-amber-700">falta: {step.missing.map((m) => ({ owner: "dono", team: "equipa", planned_start: "data" })[m] ?? m).join(", ")}</span>
                    )}
                  </div>
                  {step.items && step.items.length > 0 && (
                    <ul className="ml-3 mt-0.5 space-y-0.5 text-muted-foreground">
                      {step.items.map((it) => (
                        <li key={it.label} className={cn(it.checked && "text-foreground")}>{it.checked ? "☑" : "☐"} {it.label}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>

      {guide.payments.rows.length > 0 && (
        <div className="space-y-1 border-t pt-3 border-border">
          <h3 className="text-xs font-semibold">Pagamentos{guide.payments.terms_name && ` · ${guide.payments.terms_name}`}</h3>
          <ul className="space-y-0.5 text-xs">
            {guide.payments.rows.map((p) => (
              <li key={p.milestone_id} className="flex flex-wrap justify-between gap-2">
                <span>
                  {p.label ?? "Prestação"}
                  {p.percent != null && ` · ${p.percent}%`}
                </span>
                <span className="text-muted-foreground">
                  {/* Sem «Dados financeiros» o valor vem a null: «—». */}
                  {`${fmtEur(p.amount)} · `}
                  {PAYMENT_STATE[p.state] ?? p.state}
                  {p.paid_at && ` ${when(p.paid_at)}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
